<script setup>
import { computed, ref } from 'vue';
import { SHARED_ROUTE_PALETTE } from '../utils/palette';
import './SpinningWheel.css';

const props = defineProps({
  routes: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['route-select']);

const isSpinning = ref(false);
const rotationDeg = ref(0);
const topRouteName = ref('');
const titleFlashKey = ref(0);

const wedgeColors = SHARED_ROUTE_PALETTE;

const wheelBackground = computed(() => {
  const routeCount = props.routes.length;
  if (!routeCount) {
    return '#d9d9d9';
  }

  const segmentAngle = 360 / routeCount;
  const segments = props.routes.map((_, index) => {
    const start = (index * segmentAngle).toFixed(2);
    const end = ((index + 1) * segmentAngle).toFixed(2);
    const color = wedgeColors[index % wedgeColors.length];
    return `${color} ${start}deg ${end}deg`;
  });

  return `conic-gradient(${segments.join(', ')})`;
});

const wheelStyle = computed(() => ({
  transform: `rotate(${rotationDeg.value}deg)`,
  background: wheelBackground.value,
}));

const normalizeDeg = (value) => ((value % 360) + 360) % 360;

const routeIndexAtTop = (rotation, routeCount) => {
  if (!routeCount) {
    return 0;
  }

  const segmentAngle = 360 / routeCount;
  const normalized = normalizeDeg(270 - rotation);
  return Math.floor(normalized / segmentAngle) % routeCount;
};

const updateTopRouteName = (rotation) => {
  const activeIndex = routeIndexAtTop(rotation, props.routes.length);
  const route = props.routes[activeIndex];
  if (!route) {
    return;
  }

  if (topRouteName.value !== route.name) {
    topRouteName.value = route.name;
    titleFlashKey.value += 1;
  }
};

const easeOutQuart = (t) => 1 - (1 - t) ** 4;

const spinWheel = () => {
  if (!props.routes.length || isSpinning.value) {
    return;
  }

  isSpinning.value = true;
  const spinDuration = Math.random() * 1400 + 5000;
  const randomIndex = Math.floor(Math.random() * props.routes.length);
  const segmentAngle = 360 / props.routes.length;

  const startRotation = rotationDeg.value;
  const startMod = normalizeDeg(startRotation);
  const targetCenterAngle = randomIndex * segmentAngle + segmentAngle / 2;
  const targetRotationMod = normalizeDeg(270 - targetCenterAngle);
  const modDelta = normalizeDeg(targetRotationMod - startMod);
  const fullTurns = Math.floor(Math.random() * 3) + 7;
  const endRotation = startRotation + fullTurns * 360 + modDelta;
  const startedAt = performance.now();

  const tick = (now) => {
    const elapsed = now - startedAt;
    const progress = Math.min(elapsed / spinDuration, 1);
    const eased = easeOutQuart(progress);
    const currentRotation = startRotation + (endRotation - startRotation) * eased;

    rotationDeg.value = currentRotation;
    updateTopRouteName(currentRotation);

    if (progress < 1) {
      requestAnimationFrame(tick);
      return;
    }

    isSpinning.value = false;
    emit('route-select', props.routes[randomIndex]);
  };

  requestAnimationFrame(tick);
};

updateTopRouteName(rotationDeg.value);
</script>

<template>
  <div class="spinning-wheel-container">
    <div
      class="top-pointer"
      aria-hidden="true"
    />
    <div
      class="wheel"
      :class="{ spinning: isSpinning }"
      :style="wheelStyle"
      @click="spinWheel"
    />
    <Transition
      name="title-flash"
      mode="out-in"
    >
      <p
        :key="titleFlashKey"
        class="live-route-title"
      >
        {{ topRouteName }}
      </p>
    </Transition>
    <p>Click the wheel to pick a route</p>
  </div>
</template>
